import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import Modal from '../../components/shared/Modal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import { getTransporterDrivers, createDriver, updateDriver, deleteDriver } from '../../services/firestore/driverService';

const MyDrivers = ({ userData }) => {
    const { user, isOwner } = usePermissions(userData);
    const { success, error: toastError } = useToast();

    const [loading, setLoading] = useState(true);
    const [drivers, setDrivers] = useState([]);

    // Modal & Form
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        phone: '',
        email: '',
        idNumber: '',
        licenseNumber: '',
        address: '',
        photoUrl: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);

    // Dialog State
    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        action: null
    });

    const transporterId = user?.transporterId || user?.scope?.transporterId;

    useEffect(() => {
        if (user?.tenantId && transporterId) {
            loadData();
        } else {
            setLoading(false);
        }
    }, [user?.tenantId, transporterId]);

    const loadData = async () => {
        setLoading(true);
        console.log('[MyDrivers] Loading data for:', { tenantId: user.tenantId, transporterId });
        try {
            const data = await getTransporterDrivers(transporterId, user.tenantId);
            setDrivers(data);
        } catch (err) {
            console.error('[MyDrivers] Load error:', err);
            console.error('[MyDrivers] Current User Data:', user);
            toastError("Sürücü listesi yüklenirken hata oluştu: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setSelectedDriver(null);
        setFormData({
            name: '',
            surname: '',
            phone: '',
            email: '',
            idNumber: '',
            licenseNumber: '',
            address: '',
            photoUrl: ''
        });
        setIsModalOpen(true);
    };

    const handleEdit = (driver) => {
        setSelectedDriver(driver);
        setFormData({
            name: driver.name || '',
            surname: driver.surname || '',
            phone: driver.phone || '',
            email: driver.email || '',
            idNumber: driver.idNumber || '',
            licenseNumber: driver.licenseNumber || '',
            address: driver.address || '',
            photoUrl: driver.photoUrl || ''
        });
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                transporterId: transporterId,
                updatedAt: new Date()
            };

            if (selectedDriver) {
                await updateDriver(selectedDriver.id, payload, user.tenantId);
                success('Sürücü bilgileri güncellendi.');
            } else {
                await createDriver(payload, user.tenantId);
                success('Sürücü başarıyla eklendi.');
            }

            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError("İşlem başarısız: " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = (driverId, name) => {
        setConfirmConfig({
            isOpen: true,
            title: 'Sürücüyü Sil',
            message: `${name} isimli sürücüyü silmek istediğinize emin misiniz?`,
            type: 'error',
            action: async () => {
                try {
                    await deleteDriver(driverId, user.tenantId);
                    success('Sürücü silindi.');
                    loadData();
                } catch (err) {
                    toastError("Silme işlemi başarısız.");
                }
            }
        });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, photoUrl: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    if (!transporterId && !isOwner) {
        return (
            <div className="container pt-xl text-center">
                <div className="card-premium p-xl">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: 'var(--text-primary)' }}>Yetki Hatası</h3>
                    <p style={{ color: 'var(--text-secondary)' }}>Nakliye Firması yetkiniz bulunmamaktadır. (Transporter ID Eksik)</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container pt-lg">
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h1 style={{ color: 'var(--text-primary)' }}>Sürücüler</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Hizmet veren sürücü listenizi yönetin.</p>
                </div>
                <Button variant="primary" onClick={handleOpenModal}>+ Yeni Sürücü Ekle</Button>
            </div>

            <div className="card-premium glow-theme" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div className="p-xl text-center" style={{ color: 'var(--text-primary)' }}>Yükleniyor...</div>
                ) : drivers.length === 0 ? (
                    <div className="p-xl text-center" style={{ color: 'var(--text-secondary)' }}>
                        Henüz kayıtlı sürücü bulunmuyor.
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Foto</th>
                                <th>Ad Soyad</th>
                                <th>İletişim</th>
                                <th>Kimlik/Ehliyet</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drivers.map(d => (
                                <tr key={d.id}>
                                    <td>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            background: 'rgba(255,255,255,0.05)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px solid rgba(255,255,255,0.1)'
                                        }}>
                                            {d.photoUrl ? (
                                                <img src={d.photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <span style={{ fontSize: '1.2rem', filter: 'grayscale(1)', opacity: 0.5 }}>👤</span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{d.name} {d.surname}</td>
                                    <td>
                                        <div className="flex flex-col">
                                            <span style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{d.phone || '-'}</span>
                                            <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{d.email || '-'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex flex-col">
                                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>ID: {d.idNumber || '-'}</span>
                                            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>E: {d.licenseNumber || '-'}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex gap-sm">
                                            <Button size="xs" variant="secondary" onClick={() => handleEdit(d)}>Düzenle</Button>
                                            <Button size="xs" variant="danger" onClick={() => handleDelete(d.id, d.name)}>Sil</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedDriver ? "Sürücü Düzenle" : "Yeni Sürücü Kaydı"}>
                <form onSubmit={handleSave} className="p-md">
                    <div className="flex gap-md mb-md">
                        <div style={{ width: '100px' }}>
                            <div
                                onClick={() => document.getElementById('photo-upload').click()}
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '12px',
                                    border: '2px dashed var(--border-color)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    background: 'var(--bg-secondary)'
                                }}
                            >
                                {formData.photoUrl ? (
                                    <img src={formData.photoUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <>
                                        <span style={{ fontSize: '1.5rem', filter: 'grayscale(1)', opacity: 0.5 }}>📷</span>
                                        <span style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '4px' }}>Fotoğraf</span>
                                    </>
                                )}
                            </div>
                            <input
                                id="photo-upload"
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handlePhotoChange}
                            />
                        </div>
                        <div className="flex-1 space-y-md">
                            <div className="grid grid-cols-2 gap-md">
                                <div className="form-group">
                                    <label className="form-label">Ad</label>
                                    <input
                                        required
                                        type="text"
                                        className="input w-full"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Soyad</label>
                                    <input
                                        required
                                        type="text"
                                        className="input w-full"
                                        value={formData.surname}
                                        onChange={e => setFormData({ ...formData, surname: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-md">
                                <div className="form-group">
                                    <label className="form-label">Telefon</label>
                                    <input
                                        required
                                        type="text"
                                        className="input w-full"
                                        placeholder="5XX..."
                                        value={formData.phone}
                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">E-Posta</label>
                                    <input
                                        type="email"
                                        className="input w-full"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-md mb-md">
                        <div className="form-group">
                            <label className="form-label">Kimlik No (Opsiyonel)</label>
                            <input
                                type="text"
                                className="input w-full"
                                value={formData.idNumber}
                                onChange={e => setFormData({ ...formData, idNumber: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Ehliyet No</label>
                            <input
                                type="text"
                                className="input w-full"
                                value={formData.licenseNumber}
                                onChange={e => setFormData({ ...formData, licenseNumber: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="form-group mb-xl">
                        <label className="form-label">İkamet Adresi (Opsiyonel)</label>
                        <textarea
                            className="input w-full"
                            rows={2}
                            style={{ resize: 'none' }}
                            value={formData.address}
                            onChange={e => setFormData({ ...formData, address: e.target.value })}
                        />
                    </div>
                    <div className="flex justify-end gap-md mt-xl">
                        <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                        <Button type="submit" variant="primary" loading={isSaving}>Kaydet</Button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                {...confirmConfig}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={async () => {
                    if (confirmConfig.action) await confirmConfig.action();
                    setConfirmConfig(prev => ({ ...prev, isOpen: false }));
                }}
            />
        </div>
    );
};

export default MyDrivers;
